/**
 * StatsVideoScreen — assembled video (deferred) mode layout.
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ HEADER  back  game info  score  period  online  save │
 *   ├──────┬───────────────────────────┬───────────────────┤
 *   │      │                           │                   │
 *   │BENCH │     VideoStage (16:9)     │   ActionsPanel    │
 *   │      │                           │                   │
 *   │      ├───────────────────────────┤                   │
 *   │      │     EnhancedTimeline      │                   │
 *   └──────┴───────────────────────────┴───────────────────┘
 *
 * The shot chart drawer floats over the whole screen.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, Chip, Tooltip, ButtonBase, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import {
  VideoStage,
  EnhancedTimeline,
  ActionsPanel,
  BenchDrawer,
  MoreActionsSheet,
  ShotChartDrawer,
  ScorePanel,
  ConnectivityChip,
} from '../components';

export default function StatsVideoScreen({
  // Game info
  currentGame,
  teamName,

  // Roster
  players,
  activePlayers,
  playerTimeTracking,
  timeOnFloorByPlayerId,
  foulsByPlayerId,
  onPromote,
  onSubstitute,

  // Video player
  playerRef,
  videoUrl,
  videoPlaying,
  videoDuration,
  videoCurrentTime,
  playbackRate,
  videoOffset,
  periodOffsets,
  onProgress,
  onDuration,
  onTogglePlay,
  onSeek,
  onSkip,
  onPlaybackRateChange,
  onOffsetChange,

  // Period (read-only — derived from video time)
  currentPeriod,

  // Score
  // Local score is display-only in video mode (derived from actions).
  // Opponent quick buttons are still active and trigger recordOpponentScore.
  puntuacionLocal,
  puntuacionVisitante,
  onOpponentQuickScore,

  // Actions
  actionsLog,
  onAction,
  onUndo,
  hasLastAction,

  // Tags & qualifiers
  tags,
  activeQualifiers,
  onTagClick,
  onToggleQualifier,

  // More actions sheet
  moreSheetOpen,
  moreSheetPlayer,
  onOpenMoreSheet,
  onCloseMoreSheet,
  onMoreSheetAction,

  // Shot chart drawer
  shotChartOpen,
  shotChartAction,
  onShotChartRecorded,
  onShotChartClose,
  gameShotChartData,

  // UI
  benchCollapsed,
  onToggleBench,

  // DnD render-prop hooks (Phase 3.5)
  renderBenchItem,
  renderActiveCard,

  // Save & exit
  isOnline,
  isSaving,
  saveFailed,
  onSave,
  onExit,

  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);
  const compact = useMediaQuery('(max-height:1000px), (max-width:1920px)');
  const opponentColor = theme.actionColors.opponent;
  const rootRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }, []);

  return (
    <Box
      ref={rootRef}
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.surfaces.base,
        color: theme.text.primary,
        fontFamily: theme.typography.fontFamily,
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          height: compact ? 44 : 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 1 : 1.5,
          px: compact ? 1 : 1.5,
          borderBottom: `1px solid ${theme.surfaces.border}`,
          backgroundColor: theme.surfaces.elevated,
        }}
      >
        <IconButton onClick={onExit} aria-label="back" sx={{ color: theme.text.primary }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
            {currentGame?.game_label || teamName}
            {currentGame?.adversary_name && (
              <Typography component="span" sx={{ ml: 0.6, color: theme.text.secondary, fontWeight: 400 }}>
                vs {currentGame.adversary_name}
              </Typography>
            )}
          </Typography>
        </Box>

        <Chip
          label={`P${currentPeriod} · ${puntuacionLocal} – ${puntuacionVisitante}`}
          size="small"
          sx={{
            backgroundColor: theme.accentSoft,
            color: theme.accent,
            border: `1px solid ${theme.accentBorder}`,
            fontWeight: 700,
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
          }}
        />

        {compact && (
          <Box sx={{ display: 'flex', gap: 0.5 }} aria-label="opponent quick score">
            {[
              { code: 'OPP_FT', label: '+1', pts: 1 },
              { code: 'OPP_2PTS', label: '+2', pts: 2 },
              { code: 'OPP_3PTS', label: '+3', pts: 3 },
            ].map(({ code, label, pts }) => (
              <ButtonBase
                key={code}
                onClick={() => typeof onOpponentQuickScore === 'function' && onOpponentQuickScore(code, pts)}
                aria-label={`Opponent ${label}`}
                sx={{
                  px: 0.9,
                  height: 24,
                  borderRadius: theme.radius.pill,
                  backgroundColor: withAlpha(opponentColor, 0.14),
                  border: `1px solid ${withAlpha(opponentColor, 0.4)}`,
                  color: opponentColor,
                  fontSize: 10,
                  fontWeight: 700,
                  '&:hover': { backgroundColor: withAlpha(opponentColor, 0.26) },
                }}
              >
                {label}
              </ButtonBase>
            ))}
          </Box>
        )}

        <Tooltip title={tt('undoLast', 'Undo last action')}>
          <span>
            <IconButton
              onClick={onUndo}
              disabled={!hasLastAction}
              aria-label="undo"
              sx={{ color: hasLastAction ? theme.text.primary : theme.text.muted, opacity: hasLastAction ? 1 : 0.4 }}
            >
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isFullscreen ? tt('exitFullscreen', 'Exit fullscreen') : tt('fullscreen', 'Fullscreen')}>
          <IconButton
            onClick={toggleFullscreen}
            aria-label="toggle fullscreen"
            sx={{ color: theme.text.primary }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <ConnectivityChip
          isOnline={isOnline}
          isSaving={isSaving}
          saveFailed={saveFailed}
          t={t}
        />

        <Tooltip title={tt('save', 'Save')}>
          <IconButton
            onClick={onSave}
            disabled={isSaving}
            aria-label="save"
            sx={{
              color: theme.accent,
              backgroundColor: theme.accentSoft,
              border: `1px solid ${theme.accentBorder}`,
              '&:hover': { backgroundColor: withAlpha(theme.accent, 0.24) },
            }}
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* MAIN */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {/* Bench */}
          <BenchDrawer
            players={players}
            collapsed={benchCollapsed}
            onToggleCollapse={onToggleBench}
            onPromote={onPromote}
            renderItem={renderBenchItem}
            t={t}
          />

          {/* Video + timeline column */}
          <Box
            sx={{
              flex: isFullscreen ? '1 1 auto' : '1 1 60%',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRight: `1px solid ${theme.surfaces.border}`,
            }}
          >
            <Box sx={{ flex: '1 1 0', minHeight: 0, p: compact ? 0.5 : 2, overflow: 'hidden', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', '& > *': { width: '100%' } }}>
              <VideoStage
                playerRef={playerRef}
                videoUrl={videoUrl}
                videoPlaying={videoPlaying}
                videoDuration={videoDuration}
                videoCurrentTime={videoCurrentTime}
                playbackRate={playbackRate}
                videoOffset={videoOffset}
                onProgress={onProgress}
                onDuration={onDuration}
                onTogglePlay={onTogglePlay}
                onSeek={onSeek}
                onSkip={onSkip}
                onPlaybackRateChange={onPlaybackRateChange}
                onOffsetChange={onOffsetChange}
                height={isFullscreen ? 'calc(100vh - 380px)' : 'auto'}
              />
              </Box>
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <EnhancedTimeline
                actions={actionsLog}
                videoDuration={videoDuration}
                currentTime={videoCurrentTime}
                onSeek={onSeek}
                periodOffsets={periodOffsets}
                height={compact ? 60 : 110}
              />
            </Box>
          </Box>

          {/* Side column */}
          <Box sx={{
            flex: isFullscreen ? '0 0 220px' : (compact ? '1 1 38%' : '1 1 40%'),
            minWidth: isFullscreen ? 220 : (compact ? 300 : 320),
            display: 'flex',
            flexDirection: 'column',
          }}>
            {!compact && (
              <Box sx={{ p: 1.5, pb: 0 }}>
                <ScorePanel
                  teamName={teamName}
                  opponentName={currentGame?.adversary_name}
                  localScore={puntuacionLocal}
                  opponentScore={puntuacionVisitante}
                  disabled
                  showOpponentQuickButtons
                  onOpponentQuickScore={onOpponentQuickScore}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ActionsPanel
                compact={compact}
                activePlayers={activePlayers}
                tags={tags}
                activeQualifiers={activeQualifiers}
                onAction={onAction}
                onTagClick={onTagClick}
                onToggleQualifier={onToggleQualifier}
                onMore={onOpenMoreSheet}
                onSubstitute={onSubstitute}
                playerTimeTracking={playerTimeTracking}
                timeOnFloorByPlayerId={timeOnFloorByPlayerId}
                fouls={foulsByPlayerId}
                renderPlayerCard={renderActiveCard}
                showPlayers={!isFullscreen}
                t={t}
              />
            </Box>
          </Box>
        </Box>

        {/* Bottom strip: player cards in fullscreen */}
        {isFullscreen && (
          <Box sx={{
            flex: '0 0 170px',
            borderTop: `1px solid ${theme.surfaces.border}`,
            backgroundColor: theme.surfaces.elevated,
          }}>
            <ActionsPanel
              compact
              activePlayers={activePlayers}
              onAction={onAction}
              onMore={onOpenMoreSheet}
              onSubstitute={onSubstitute}
              playerTimeTracking={playerTimeTracking}
              timeOnFloorByPlayerId={timeOnFloorByPlayerId}
              fouls={foulsByPlayerId}
              renderPlayerCard={renderActiveCard}
              showTags={false}
              showQualifiers={false}
              playersLayout="row"
              t={t}
            />
          </Box>
        )}
      </Box>

      <MoreActionsSheet
        open={moreSheetOpen}
        onClose={onCloseMoreSheet}
        player={moreSheetPlayer}
        onAction={onMoreSheetAction}
        t={t}
      />

      <ShotChartDrawer
        open={shotChartOpen}
        onClose={onShotChartClose}
        shotChartAction={shotChartAction}
        onShotRecorded={onShotChartRecorded}
        existingShots={gameShotChartData}
        t={t}
      />
    </Box>
  );
}
